/**
 * Phase A mock types — mirror the eventual `FinancingApplication`
 * schema so the only Phase B work on the UI side is swapping
 * `const rows = [...]` for a TanStack Query call. No shared schema
 * import yet — that arrives with Phase B.
 */

export type TermDurationMock =
  | { value: 30; unit: 'days'; label: '30 days' }
  | { value: 60; unit: 'days'; label: '60 days' }
  | { value: 90; unit: 'days'; label: '90 days' }
  | { value: 6; unit: 'months'; label: '6 months' }
  | { value: 1; unit: 'year'; label: '1 year' };

export const TERM_DURATION_OPTIONS: TermDurationMock[] = [
  { value: 30, unit: 'days', label: '30 days' },
  { value: 60, unit: 'days', label: '60 days' },
  { value: 90, unit: 'days', label: '90 days' },
  { value: 6, unit: 'months', label: '6 months' },
  { value: 1, unit: 'year', label: '1 year' },
];

export type FleetTierMock =
  | 'lt_10'
  | '11_20'
  | '21_50'
  | '51_100'
  | '101_200'
  | '201_500'
  | 'gt_500';

export const FLEET_TIER_LABELS: Record<FleetTierMock, string> = {
  lt_10: 'Fewer than 10',
  '11_20': '11 – 20',
  '21_50': '21 – 50',
  '51_100': '51 – 100',
  '101_200': '101 – 200',
  '201_500': '201 – 500',
  gt_500: 'More than 500',
};

export type RevenueTierMock =
  | 'lt_1m'
  | '1m_3m'
  | '3m_6m'
  | '6m_8m'
  | '8m_10m'
  | 'gt_10m';

export const REVENUE_TIER_LABELS: Record<RevenueTierMock, string> = {
  lt_1m: 'Less than 1M',
  '1m_3m': '1M – 3M',
  '3m_6m': '3M – 6M',
  '6m_8m': '6M – 8M',
  '8m_10m': '8M – 10M',
  gt_10m: 'More than 10M',
};

export type FinancingNeedMock =
  | 'invoicing'
  | 'trucks'
  | 'working_capital'
  | 'fuel'
  | 'other';

export const FINANCING_NEED_LABELS: Record<FinancingNeedMock, string> = {
  invoicing: 'Invoice financing',
  trucks: 'Truck loans',
  working_capital: 'Working capital',
  fuel: 'Fuel financing',
  other: 'Other',
};

export type FinancingProfileMock = {
  fleetTier: FleetTierMock;
  annualRevenueTier: RevenueTierMock;
  financingNeeds: FinancingNeedMock[];
  ownsAllTrucks: boolean;
  trucksOwnedCount?: number;
  hasLoans: boolean;
  monthlyFinancingPayment?: number;
  /**
   * Self-reported profitability flag — optional because carriers
   * may skip the question. Present iff the carrier answered.
   */
  isProfitable?: boolean;
  /**
   * Uploaded financial statements (P&L, balance sheet, etc.).
   * Optional — many carriers apply without docs. Each entry carries
   * the Cloudinary `{ id, url }` pair so the UI can render a link
   * straight to the asset.
   */
  financialStatements?: { id: string; url: string }[];
};

export type InvoiceSourceMock = 'hero' | 'external';

/**
 * Snapshot of a Hero-issued invoice the carrier picked from the
 * apply-form's "Use a Hero invoice" tab. The id resolves the full
 * `Invoice` server-side at submit time; the rest of the keys are
 * a flat display projection so the wizard's review step and the
 * lender packet don't need to re-fetch.
 */
export type HeroInvoiceMock = {
  id: string;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  currency: 'SAR';
  expectedDueDate: string;
};

export type InvoiceMock = {
  source: InvoiceSourceMock;
  id?: string;
  clientName: string;
  amount: number;
  currency: 'SAR';
  expectedDueDate: string;
  invoiceNumber?: string;
  documentUrl?: string;
};

export type OfferDecisionMock =
  | 'pending'
  | 'interested'
  | 'offered'
  | 'declined'
  | 'accepted';

export type OfferTermsMock = {
  advancedAmount: number;
  feeAmount: number;
  feePercentage: number;
  term: TermDurationMock;
  repaymentDate?: string;
  recourse: boolean;
  notes?: string;
};

export type OfferDeclineReasonMock =
  | 'risk_too_high'
  | 'incomplete_profile'
  | 'customer_concentration'
  | 'outside_criteria'
  | 'other';

export const DECLINE_REASON_LABELS: Record<OfferDeclineReasonMock, string> = {
  risk_too_high: 'Risk too high',
  incomplete_profile: 'Incomplete profile',
  customer_concentration: 'Customer concentration',
  outside_criteria: 'Outside our criteria',
  other: 'Other',
};

export type OfferMock = {
  id: string;
  /**
   * Hex string id of the `Lender` doc that owns this offer row.
   * Resolves directly to `application.offers[i].lender._id` on the
   * real schema. Use this to look up display info (logo, brand
   * color, tagline) from `useActiveLendersApi`.
   */
  lenderId: string;
  lenderName: string;
  decision: OfferDecisionMock;
  decidedAt?: string;
  terms?: OfferTermsMock;
  isCounterOffer?: boolean;
  declineReason?: OfferDeclineReasonMock;
  declineNotes?: string;
};

export type ApplicationStatusMock =
  | 'pending_review'
  | 'submitted'
  | 'declined'
  | 'accepted'
  | 'funded'
  | 'awaiting_repayment_approval'
  | 'repaid'
  | 'defaulted'
  | 'cancelled';

export type ApplicationMock = {
  id: string;
  ref: string;
  invoice: InvoiceMock;
  requestedAmount: number;
  requestedTerm: TermDurationMock;
  profileSnapshot: FinancingProfileMock;
  status: ApplicationStatusMock;
  offers: OfferMock[];
  /**
   * Admin-curated markers from `FinancingApplication.tags[]`.
   * Surfaced so the row can render the same `late` / `flagged`
   * chips the admin panel does.
   */
  tags?: ('late' | 'overdue' | 'flagged')[];
  /**
   * Derived in the adapter (`isFinancingApplicationLate`) — true
   * when the repayment is past the configured grace window OR an
   * admin manually applied a `late` / `overdue` tag. Cached on
   * the mock so the UI doesn't recompute on every render.
   */
  isLate?: boolean;
  /** Resolved repayment due date when known (funded or later). */
  dueDate?: string;
  /**
   * Server-stamped `outcome.repaidAt`. Used by the applications
   * table to color the "Due" column when the loan closed —
   * compared against `dueDate` to decide repaid-early /
   * on-time / paid-late copy. Empty for non-`repaid` rows.
   */
  repaidAt?: string;
  /**
   * Last rejection reason from a lender / admin review of the
   * carrier's repayment receipt, surfaced when the application is
   * back in `funded` AND no fresh receipt has been re-uploaded
   * yet. Sourced from `outcome.notes` server-side; cleared the
   * moment a new receipt lands. Used by the carrier-side
   * rejection banner above the Mark-as-paid CTA so the carrier
   * sees what to fix without bouncing through the lender.
   */
  lastRejectionNotes?: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Status surfaced in the lender's table. Derived from
 * `offers[].decision` + the application's overall status:
 * - `pending`       — lender has not decided yet
 * - `you_approved`  — lender approved; carrier hasn't picked
 * - `you_declined`  — lender declined
 * - `won`           — lender approved AND carrier accepted this offer
 * - `lost`          — carrier accepted a different lender's offer
 * - `funded`        — won AND money disbursed
 * - `repaid`        — funded AND fully repaid
 * - `defaulted`     — funded AND carrier defaulted
 */
export type LenderRowStatusMock =
  | 'pending'
  | 'you_approved'
  | 'you_declined'
  | 'won'
  | 'lost'
  | 'funded'
  | 'repaid'
  | 'defaulted';

export type LenderSignalsMock = {
  ordersLast12mo: number;
  grossRevenueLast12mo: number;
  avgDSO: number;
  topCustomerSharePct: number;
  ibanVerified: boolean;
  crActive: boolean;
  vatActive: boolean;
  currentARTotal: number;
};

export type LenderInboxRowMock = {
  id: string;
  ref: string;
  carrier: {
    name: string;
    fleetTier: FleetTierMock;
    annualRevenueTier: RevenueTierMock;
    monthsActive: number;
  };
  invoice: InvoiceMock;
  requestedAmount: number;
  requestedTerm: TermDurationMock;
  profileSnapshot: FinancingProfileMock;
  signals: LenderSignalsMock;
  status: LenderRowStatusMock;
  myTerms?: OfferTermsMock;
  declineReason?: OfferDeclineReasonMock;
  declineNotes?: string;
  decidedAt?: string;
  receivedAt: string;
};
